import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

/**
 * A FOOLDALROL ATHOZOTT FAJLOK.
 *
 * ────────────────────────────────────────────────────────────────────
 * MIERT KELL EZ EGYALTALAN
 *
 * A latogato a FOOLDALON valaszt fajlt, a feldolgozas viszont mar a
 * `/arbecsles` oldalon tortenik. A ket oldal kozott a `File` objektumoknak
 * at kell jutniuk - ugy, hogy a fajlvalaszto masodszor NE nyiljon ki.
 *
 * A `File` NEM sorosithato: sem `sessionStorage`-ba, sem az URL-be nem
 * fer bele. A tartalma a lemezen van, es a bongeszo csak egy elo
 * hivatkozast ad ra. Ezert MEMORIABAN, React-allapotban tartjuk, a
 * Root FOLOTT - igy az utvonalvaltas (ami a Root gyerekeit ujra
 * mountolja) nem torli.
 *
 * KOVETKEZMENY, AMIT VALLALUNK: egy OLDALFRISSITES elvesziti oket. Ez
 * helyes viselkedes - a bongeszo sem tart meg fajlvalasztast frissites
 * utan. A `/arbecsles` oldal ezert onmagaban is teljes erteku feltoltot
 * ad, nem csak "atvevo" kepernyot.
 * ────────────────────────────────────────────────────────────────────
 */

export interface DraftFiles {
  /** A meg fel nem dolgozott fajlok. */
  files: File[]
  /** A fooldali feltolto ezt hivja, mielott atnavigal. */
  handOver: (files: File[]) => void
  /**
   * Az arbecsles-oldal EGYSZER veszi at oket.
   *
   * Atvetel utan urul a keszlet: kulonben egy kesobbi vissza-elore
   * navigacio ujra beszurna ugyanazokat a fajlokat.
   */
  take: () => File[]
  /** Volt-e egyaltalan atadas - ebbol tudjuk, hogy a fooldalrol jottunk. */
  handedOver: boolean
}

const DraftContext = createContext<DraftFiles | null>(null)

export function DraftProvider({ children }: { children: React.ReactNode }): JSX.Element {
  /*
    A LISTA REF-BEN AL, NEM ALLAPOTBAN.

    A `take()`-nek AZONNAL a jelenlegi listat kell visszaadnia, meg a
    kovetkezo render elott. Egy `setFiles(current => ...)` frissitoje viszont
    csak a render fazisban fut le - a hivo addig ures tombot kapna, es a
    fooldalrol athozott fajlok csendben eltunnenek.

    Az allapot itt csak arra kell, hogy az ujrarajzolas megtortenjen.
  */
  const filesRef = useRef<File[]>([])
  const handedOver = useRef(false)
  const [revision, bump] = useState(0)

  const handOver = useCallback((next: File[]) => {
    filesRef.current = next
    handedOver.current = true
    bump((n) => n + 1)
  }, [])

  const take = useCallback((): File[] => {
    const taken = filesRef.current
    filesRef.current = []
    if (taken.length) bump((n) => n + 1)
    return taken
  }, [])

  const value = useMemo<DraftFiles>(
    () => ({
      files: filesRef.current,
      handOver,
      take,
      handedOver: handedOver.current
    }),
    // A `revision` szandekosan szerepel: ez jelzi, hogy a ref valtozott.
    [revision, handOver, take]
  )

  return <DraftContext.Provider value={value}>{children}</DraftContext.Provider>
}

/**
 * A draft keszlet.
 *
 * Provider nelkul is mukodik (ures keszlettel): igy egy komponens
 * onmagaban is tesztelheto, es egy elfelejtett Provider nem fehér
 * kepernyot ad, hanem csak annyit, hogy nincs athozott fajl.
 */
export function useDraftFiles(): DraftFiles {
  const ctx = useContext(DraftContext)
  return (
    ctx ?? {
      files: [],
      handOver: () => undefined,
      take: () => [],
      handedOver: false
    }
  )
}

/**
 * A főoldalról érkező fájlok StrictMode-biztos átvétele.
 *
 * A tárat nem szabad renderelés közben üríteni: fejlesztői StrictMode-ban
 * React kétszer meghívhatja a renderfüggvényt, így a második kör már üres
 * listát kapna. Előbb stabil referenciába mentjük a fájlokat, és csak a
 * commit után jelezzük a tárnak, hogy az átadás megtörtént.
 */
export function useTransferredFiles(): File[] {
  const draft = useDraftFiles()
  const transferred = useRef<File[] | null>(null)

  if (transferred.current === null) transferred.current = draft.files

  useEffect(() => {
    if (transferred.current?.length) draft.take()
  }, [draft.take])

  return transferred.current
}
